import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { readJsonOnce } from '../utils/safeFetch';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_FLAG = 'ludy_has_session'; // optimistic auth hint (no PII)

// Read a non-PII hint that "we had an authenticated session before reload"
// so ProtectedRoute does not redirect to landing during the in-flight /me check.
function hadSession() {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

function setHadSession(value) {
  try {
    if (typeof window === 'undefined') return;
    if (value) {
      window.localStorage.setItem(SESSION_FLAG, '1');
    } else {
      window.localStorage.removeItem(SESSION_FLAG);
    }
  } catch {
    // ignore — Safari private mode etc.
  }
}

// Fetch with abortable timeout + retries on transient errors — auth/me on
// first page load can race against Sentry instrumentation, slow cold starts,
// or a saturated global rate-limit bucket (429). 4xx other than 429 is
// authoritative and must propagate immediately.
async function fetchWithTimeout(url, options = {}, timeoutMs = 8000, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(url, { ...options, signal: ctrl.signal });
      clearTimeout(timer);
      const isTransient = r.status === 429 || r.status >= 500;
      if (isTransient && attempt < retries) {
        // Honour Retry-After when present (Paddle/Resend style), otherwise
        // exponential backoff capped at 1.5s so we don't starve the UI.
        const ra = parseInt(r.headers.get('retry-after') || '', 10);
        const delay = Number.isFinite(ra) && ra > 0
          ? Math.min(ra * 1000, 1500)
          : 200 * Math.pow(2, attempt);
        await new Promise(res => setTimeout(res, delay));
        continue;
      }
      return r;
    } catch (e) {
      clearTimeout(timer);
      if (attempt >= retries) throw e;
      await new Promise(res => setTimeout(res, 200 * Math.pow(2, attempt)));
    }
  }
  throw new Error('Network unreachable');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Start "loading" only if we have no prior session hint — when we DO have a
  // hint, render protected routes optimistically while /me confirms in
  // background. Prevents the "refresh kicks you to landing" UX bug.
  const [loading, setLoading] = useState(true);
  // Tracks whether /api/auth/me has returned an AUTHORITATIVE answer
  // (200 with user, or 401/403). Transient errors (429, 5xx, network) do NOT
  // flip this — so optimisticAuth keeps the user on the protected route
  // instead of bouncing them to landing.
  const [authoritativeAnswered, setAuthoritativeAnswered] = useState(false);

  // Keep Sentry's user context in sync with auth state.
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id || user.user_id, email: user.email });
      setHadSession(true);
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/api/auth/me`,
        { credentials: 'include' },
        8000,
        2
      );

      if (response.ok) {
        const userData = await readJsonOnce(response);
        if (userData && typeof userData === 'object') {
          setUser(userData);
          setHadSession(true);
        } else {
          setUser(null);
          setHadSession(false);
        }
        setAuthoritativeAnswered(true);
      } else if (response.status === 401 || response.status === 403) {
        // Auth is authoritative: clear the optimistic flag.
        setUser(null);
        setHadSession(false);
        setAuthoritativeAnswered(true);
      } else {
        // Transient error after retries (429, 5xx). Preserve optimistic state
        // so the user stays on the protected route; the next interaction will
        // retry organically. DO NOT flip authoritativeAnswered.
        console.error('Auth check transient error:', response.status);
        if (!hadSession()) {
          setUser(null);
          setAuthoritativeAnswered(true);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Network error — preserve optimistic state if we had a session before.
      if (!hadSession()) {
        setUser(null);
        setAuthoritativeAnswered(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
    setHadSession(false);
    window.location.href = '/';
  };

  const updateRole = async (newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/role`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        const updatedUser = await readJsonOnce(response);
        if (updatedUser && typeof updatedUser === 'object') {
          setUser(updatedUser);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Role update failed:', error);
      return false;
    }
  };

  // Optimistic auth: if we have a prior session hint AND /me has not yet
  // produced an authoritative 401/403, expose isAuthenticated as true so
  // refresh doesn't bounce the user to landing — neither during the in-flight
  // /me check NOR when /me returns a transient 429/5xx that we've already
  // retried. The real user object replaces this within ~200ms on the happy
  // path; on transient backend errors we hold the UI together until the next
  // interaction retries.
  const optimisticAuth = !user && !authoritativeAnswered && hadSession();

  const value = {
    user,
    setUser,
    loading,
    login,
    logout,
    updateRole,
    checkAuth,
    isAuthenticated: !!user || optimisticAuth,
    isManager: user?.role === 'manager'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
